import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import { showDialog } from "@widgets/CommonDialog";
import { getInitPopUpData } from "@shared/utils/CommonUtils";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_SCT_TAX_REG_001_POP_01 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
      grid3: React.createRef(),
      grid4: React.createRef(),
    });

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
      grid4: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
      grid4: [],
    });

    const [popupParent, setPopupParent] = useState<any>(null);
    const [selectedTabPage, setTapPage] = useState<string>("1");

    useImperativeHandle(ref, () => ({
      onSearch,
      checkValidity: async () => {
        let isSame: boolean = false;
        const checkedRows =
          selectedTabPage === "1"
            ? gridRefs.current["grid1"].current.getCheckedRows()
            : gridRefs.current["grid3"].current.getCheckedRows();
        if (checkedRows.length <= 1) return true;

        const cdCustValue = checkedRows[0].cd_cust; // 외주처
        const fgTrnsValue = checkedRows[0].fg_trns; // 거래구분
        const cdSctValue = checkedRows[0].cd_sct; // 외주형태
        const fgVatValue = checkedRows[0].fg_vat; // 과세구분
        const cdExchValue = checkedRows[0].cd_exch; // 환종

        isSame = checkedRows.every(
          (item: any) =>
            item.cd_cust === cdCustValue &&
            item.fg_trns === fgTrnsValue &&
            item.cd_sct === cdSctValue &&
            item.fg_vat === fgVatValue &&
            item.cd_exch === cdExchValue
        );

        if (!isSame) {
          await showDialog(
            "alter",
            "외주처, 거래구분, 외주형태, 과세구분, 환종 정보가 동일한 건만 적용 가능합니다."
          );
          return false;
        } else {
          return true;
        }
      },
      getOkayData: () => {
        let result;
        if (selectedTabPage === "1") {
          result = [
            gridRefs.current["grid1"].current?.getCheckedRows() || [],
            gridRefs.current["grid2"].current?.getCheckedRows() || [],
          ];
        } else {
          result = [
            gridRefs.current["grid3"].current?.getCheckedRows() || [],
            gridRefs.current["grid4"].current?.getCheckedRows() || [],
          ];
        }
        return {
          rtnData: result,
        };
      },
    }));

    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_SCT_TAX_REG_001_POP_01",
            {}
          );
          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormControlData(rtn.formControlData);
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
            grid2: rtn.grid.grid2,
            grid3: rtn.grid.grid3,
            grid4: rtn.grid.grid4,
          });
        } catch (error) {}
      };
      fetchform();
    }, []);

    const onSearch = async () => {
      try {
        const params = {
          id: "sn_sct_tax_reg_001_pop_001_01",
          params: searchRef.current.getFormdata(),
        };

        gridRefs.current["grid1"].current.resetUpdate();
        gridRefs.current["grid2"].current.resetUpdate();
        gridRefs.current["grid3"].current.resetUpdate();
        gridRefs.current["grid4"].current.resetUpdate();
        const data: any = await fnGetDataSet(params);
        setDataSources({
          grid1: data[0] || [],
          grid2: data[1] || [],
          grid3: data[2] || [],
          grid4: data[3] || [],
        });
      } catch (error) {}
    };

    const onTabChanged = (index: string) => {
      setTapPage(index);
    };

    const onCheckedRowChanged1 = (
      value: boolean,
      type: "row" | "header" = "row"
    ) => {
      if (type === "header") {
        gridRefs.current["grid2"].current.setCheckedAll(value);
      } else {
        gridRefs.current["grid2"].current.setCheckedAllWithFilter(value);
      }
    };

    const onCheckedRowChanged2 = (value: boolean) => {
      const checkedRows =
        gridRefs.current["grid2"].current.getCheckedFilteredRows();
      if (checkedRows.length > 0)
        gridRefs.current["grid1"].current.setCheckedFocusedRow(true, "noEvent");
      else
        gridRefs.current["grid1"].current.setCheckedFocusedRow(
          false,
          "noEvent"
        );
    };

    const onCheckedRowChanged3 = (
      value: boolean,
      type: "row" | "header" = "row"
    ) => {
      if (type === "header") {
        gridRefs.current["grid4"].current.setCheckedAll(value);
      } else {
        gridRefs.current["grid4"].current.setCheckedAllWithFilter(value);
      }
    };

    const onCheckedRowChanged4 = (value: boolean) => {
      const checkedRows =
        gridRefs.current["grid4"].current.getCheckedFilteredRows();
      if (checkedRows.length > 0)
        gridRefs.current["grid3"].current.setCheckedFocusedRow(true, "noEvent");
      else
        gridRefs.current["grid3"].current.setCheckedFocusedRow(
          false,
          "noEvent"
        );
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onCheckedRowChaged: onCheckedRowChanged1,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCheckedRowChaged: onCheckedRowChanged2,
          },
        },
        grid3: {
          columns: columns.grid3,
          dataSource: dataSources.grid3,
          events: {
            onCheckedRowChaged: onCheckedRowChanged3,
          },
        },
        grid4: {
          columns: columns.grid4,
          dataSource: dataSources.grid4,
          events: { onCheckedRowChaged: onCheckedRowChanged4 },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div className="h-full flex flex-col">
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div className="mb-2" style={{ textAlign: "center" }}>
          <SnSearchBtn
            text={"조회"}
            onClick={onSearch}
            sx={{ width: 80, marginTop: 1.5 }}
            spinner={true}
          />
        </div>

        <div className="flex-1 h-full min-h-0 space-y-1.5">
          <SnTab
            titles={["입고번호별", "외주처별"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            body={[
              <div className="h-full flex flex-col gap-y-1.5">
                <SnGrid
                  ref={gridRefs.current["grid1"]}
                  columnConfig={grids.grid1.columns}
                  rowData={grids.grid1.dataSource}
                  etcProps={{
                    rowSelection: "single",
                    ischeckboxSelection: true,
                    popupParent: popupParent,
                  }}
                  events={grids.grid1.events}
                  filter={{
                    refs: [gridRefs.current["grid2"]],
                    columns: ["no_io"],
                  }}
                />
                <SnGrid
                  ref={gridRefs.current["grid2"]}
                  columnConfig={grids.grid2.columns}
                  rowData={grids.grid2.dataSource}
                  etcProps={{
                    rowSelection: "single",
                    ischeckboxSelection: true,
                    popupParent: popupParent,
                  }}
                  events={grids.grid2.events}
                />
              </div>,

              <div className="h-full flex flex-col gap-y-1.5">
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={grids.grid3.dataSource}
                  etcProps={{
                    rowSelection: "single",
                    ischeckboxSelection: true,
                    popupParent: popupParent,
                  }}
                  events={grids.grid3.events}
                  filter={{
                    refs: [gridRefs.current["grid4"]],
                    columns: ["cd_cust"],
                  }}
                />
                <SnGrid
                  ref={gridRefs.current["grid4"]}
                  columnConfig={grids.grid4.columns}
                  rowData={grids.grid4.dataSource}
                  etcProps={{
                    rowSelection: "single",
                    ischeckboxSelection: true,
                    popupParent: popupParent,
                  }}
                  events={grids.grid4.events}
                />
              </div>,
            ]}
          ></SnTab>
        </div>
      </div>
    );
  }
);

export default SN_SCT_TAX_REG_001_POP_01;
